/**
 * Variant Image Hover Handler
 * 
 * Switches the main product image to a "front" view image on hover
 * Note: Default view = Sideways alt tag, Hover view = Front alt tag
 * based on the currently selected variant.
 */

debugLog('[VariantImageHover] ✅ Script loaded!');
debugLog('[VariantImageHover] Current URL:', window.location.href);
debugLog('[VariantImageHover] Current pathname:', window.location.pathname);

// Force immediate initialization check
window.variantImageHoverReady = false;

if (!customElements.get('variant-image-hover')) {
  debugLog('[VariantImageHover] ✅ Registering custom element...');
  customElements.define(
    'variant-image-hover',
    class VariantImageHover extends HTMLElement {
      constructor() {
        super();
        debugLog('[VariantImageHover] Constructor called');
        this.variantImageMap = {};
        this.currentVariantId = null;
        this.originalImageSrc = null;
        this.hoverImageSrc = null;
        this.mainImageElement = null;
        this.hoverContainer = null;
        this.isHovering = false;
        this.isToggled = false;
        this.viewToggleButton = null;
        this.initialized = false;
        this.observer = null;
      }
      
      connectedCallback() {
        debugLog('[VariantImageHover] Element connected to DOM');
        debugLog('[VariantImageHover] Current URL:', window.location.pathname);
        
        // Only run on teleport-ebike product page
        // Try multiple ways to get product handle
        let productHandle = '';
        const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
        if (pathMatch) {
          productHandle = pathMatch[1];
        }
        
        debugLog('[VariantImageHover] Detected product handle:', productHandle);
        
        if (productHandle !== 'teleport-ebike') {
          debugLog(`[VariantImageHover] Skipping - not teleport-ebike product (handle: "${productHandle}")`);
          return;
        }
        
        // Move init here so it works even if element is dynamically inserted
        if (!this.initialized) {
          debugLog('[VariantImageHover] Starting initialization from connectedCallback...');
          this.init();
        } else {
          debugLog('[VariantImageHover] Already initialized, skipping...');
        }
      }

      disconnectedCallback() {
        // Clean up observer when element is removed
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      init() {
        // Only run on teleport-ebike product page
        // Try multiple ways to get product handle
        let productHandle = '';
        const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
        if (pathMatch) {
          productHandle = pathMatch[1];
        }
        
        debugLog('[VariantImageHover] init() - product handle:', productHandle);
        
        if (productHandle !== 'teleport-ebike') {
          debugLog(`[VariantImageHover] Skipping init - not teleport-ebike product (handle: "${productHandle}")`);
          return;
        }
        
        if (this.initialized) {
          debugLog('[VariantImageHover] ⚠️ Already initialized in init(), skipping...');
          return;
        }
        
        debugLog('[VariantImageHover] init() called, document.readyState:', document.readyState);
        
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          debugLog('[VariantImageHover] DOM still loading, waiting for DOMContentLoaded...');
          document.addEventListener('DOMContentLoaded', () => {
            debugLog('[VariantImageHover] DOMContentLoaded fired, checking dependencies...');
            this.waitForDependencies();
          });
        } else {
          debugLog('[VariantImageHover] DOM already ready, checking dependencies immediately...');
          this.waitForDependencies();
        }
        
        // Aggressive fallback - always try to setup after 3 seconds regardless
        setTimeout(() => {
          if (!this.initialized) {
            debugWarn('[VariantImageHover] 🚨 AGGRESSIVE FALLBACK: 3s timeout, forcing setup...');
            this.setup();
          }
        }, 3000);
      }

      waitForDependencies() {
        debugLog('[VariantImageHover] Checking dependencies...');
        
        // Less strict: only need media wrapper OR product data
        const mediaWrapper = document.querySelector('.product__media-wrapper, .gallery-slider, .swiper-container, .gallery');
        const variantSelects = document.querySelector('variant-selects, variant-radios, [name="id"]');
        const productVariants = window.productVariants;
        const productData = document.querySelector('script[type="application/json"][data-product-json]');

        debugLog('[VariantImageHover] Dependencies check:', {
          mediaWrapper: !!mediaWrapper,
          variantSelects: !!variantSelects,
          productVariants: !!productVariants,
          productData: !!productData
        });

        // If we have media wrapper OR product data, proceed (less strict)
        if (mediaWrapper || productData || productVariants) {
          debugLog('[VariantImageHover] ✅ Dependencies ready, initializing in 100ms...');
          // Small delay to ensure everything is ready
          setTimeout(() => {
            debugLog('[VariantImageHover] Calling setup() now...');
            this.setup();
          }, 100);
        } else {
          // Use MutationObserver to wait for elements
          debugLog('[VariantImageHover] ⏳ Waiting for dependencies...');
          let checkCount = 0;
          this.observer = new MutationObserver(() => {
            checkCount++;
            const mediaWrapper = document.querySelector('.product__media-wrapper, .gallery-slider, .swiper-container, .gallery');
            const productVariants = window.productVariants;
            const productData = document.querySelector('script[type="application/json"][data-product-json]');

            // Less strict: proceed if we have ANY of these
            if (mediaWrapper || productVariants || productData || checkCount > 10) {
              debugLog(`[VariantImageHover] ✅ Dependencies found (check ${checkCount}), initializing...`);
              if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
              }
              setTimeout(() => {
                debugLog('[VariantImageHover] Calling setup() from observer...');
                this.setup();
              }, 100);
            }
          });

          this.observer.observe(document.body, {
            childList: true,
            subtree: true
          });

          // Fallback timeout - shorter, more aggressive
          setTimeout(() => {
            if (!this.initialized) {
              debugWarn('[VariantImageHover] ⚠️ Timeout waiting for dependencies, attempting setup anyway...');
              if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
              }
              debugLog('[VariantImageHover] Calling setup() from timeout...');
              this.setup();
            }
          }, 2000);
        }
      }

      setup() {
        if (this.initialized) {
          debugLog('[VariantImageHover] ⚠️ Already initialized, skipping...');
          return;
        }

        debugLog('[VariantImageHover] 🚀 SETUP STARTING...');
        
        // Get variant-to-image mapping from data attribute (if provided)
        const mappingData = this.dataset.variantImageMap;
        if (mappingData && mappingData !== '{}') {
          try {
            this.variantImageMap = JSON.parse(mappingData);
            debugLog('[VariantImageHover] Using provided mapping');
          } catch (e) {
            debugWarn('[VariantImageHover] Failed to parse variant image mapping:', e);
          }
        }

        // Build mapping from product data if not provided
        // Use async/await pattern instead of delays
        if (Object.keys(this.variantImageMap).length === 0) {
          this.buildVariantImageMap().then(() => {
            // Map is built, now initialize everything
            this.initializeAfterMapBuilt();
          }).catch(() => {
            // Even if map build fails, try to initialize
            this.initializeAfterMapBuilt();
          });
        } else {
          // Map already exists, initialize immediately
          this.initializeAfterMapBuilt();
        }
      }

      async initializeAfterMapBuilt() {
        // Find the main gallery image element
        this.findMainImage();

        // Set initial variant BEFORE setting up hover handlers
        this.setInitialVariant();

        // Ensure sideways view now that we have variant and map
        this.ensureSidewaysView();

        // Set up hover handlers (now hoverImageSrc should be set)
        this.setupHoverHandlers();

        // Listen for variant changes
        this.listenToVariantChanges();
        
        // Watch for image changes from custom.js or other scripts
        this.watchForImageChanges();

        this.initialized = true;
        window.variantImageHoverReady = true;
        debugLog('[VariantImageHover] ✅ SETUP COMPLETE!');
        debugLog('[VariantImageHover] 📊 Summary:', {
          variantMapSize: Object.keys(this.variantImageMap).length,
          currentVariantId: this.currentVariantId,
          hoverImageSrc: this.hoverImageSrc ? 'SET' : 'NULL',
          mainImageElement: this.mainImageElement ? 'FOUND' : 'NOT FOUND',
          viewToggleButton: this.viewToggleButton ? 'CREATED' : 'NOT CREATED'
        });
        
        // Force button to be visible if it exists
        if (this.viewToggleButton) {
          this.viewToggleButton.style.display = 'flex';
          debugLog('[VariantImageHover] 🔘 Button forced to visible');
        } else {
          debugError('[VariantImageHover] ❌ Button was NOT created!');
        }
      }
      
      watchForImageChanges() {
        // Watch for when custom.js or other scripts change the image
        // Use MutationObserver to detect src changes
        // IMPORTANT: Don't trigger on our own hover/toggle changes
        if (!this.mainImageElement) return;
        
        // Flag to track when we're making our own changes
        this._isChangingImage = false;
        
        const observer = new MutationObserver((mutations) => {
          // Skip if we're making our own changes (hover/toggle)
          if (this._isChangingImage || this.isHovering || this.isToggled) {
            return;
          }
          
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
              // Image src changed by external script, ensure it's sideways
              // Check if it's a front image using ONLY alt tag
              const alt = (this.mainImageElement.alt || '').toLowerCase();
              const altIsFront = alt.includes('front') && !alt.includes('sideways');
              
              if (altIsFront) {
                debugLog('[VariantImageHover] External script changed image to front view, switching to sideways...');
                // Small delay to let external script finish, then switch to sideways
                setTimeout(() => {
                  // Only if we're not hovering or toggled
                  if (!this.isHovering && !this.isToggled) {
                    this.findMainImage();
                    this.ensureSidewaysView();
                  }
                }, 50);
              }
            }
          });
        });
        
        observer.observe(this.mainImageElement, {
          attributes: true,
          attributeFilter: ['src']
        });
        
        // Also watch for when a new image element is created (custom.js clears and recreates)
        const wrapperObserver = new MutationObserver(() => {
          // Skip if we're hovering or toggled
          if (this.isHovering || this.isToggled) {
            return;
          }
          
          const newImg = document.querySelector('.product__media-wrapper img');
          if (newImg && newImg !== this.mainImageElement) {
            debugLog('[VariantImageHover] New image element detected, ensuring sideways view...');
            this.findMainImage();
            setTimeout(() => {
              // Only if we're not hovering or toggled
              if (!this.isHovering && !this.isToggled) {
                this.ensureSidewaysView();
                this.setupHoverHandlers();
              }
            }, 100);
          }
        });
        
        const wrapper = this.mainImageElement.closest('.product__media-wrapper');
        if (wrapper) {
          wrapperObserver.observe(wrapper, {
            childList: true,
            subtree: true
          });
        }
      }

      async buildVariantImageMap() {
        // Get variants data - try multiple sources
        let variants = [];
        
        // Strategy 1: Check window.productVariants (used by product-bundle)
        if (window.productVariants) {
          const productId = document.querySelector('[data-product-id]')?.dataset?.productId;
          if (productId && window.productVariants[productId]) {
            variants = window.productVariants[productId];
            debugLog(`[VariantImageHover] Found ${variants.length} variants from window.productVariants`);
          }
        }
        
        // Strategy 2: Look for variant-selects element
        if (variants.length === 0) {
          let variantSelects = document.querySelector('variant-selects');
          if (!variantSelects) {
            variantSelects = document.querySelector('variant-radios');
          }
          if (!variantSelects) {
            variantSelects = document.querySelector('variant-selects[data-section]');
          }
          if (!variantSelects) {
            variantSelects = document.querySelector('variant-radios[data-section]');
          }
          
          if (variantSelects) {
            const variantsScript = variantSelects.querySelector('script[type="application/json"]');
            if (variantsScript) {
              try {
                variants = JSON.parse(variantsScript.textContent);
                debugLog(`[VariantImageHover] Found ${variants.length} variants from variant-selects`);
              } catch (e) {
                debugWarn('[VariantImageHover] Failed to parse variants script:', e);
              }
            }
          }
        }
        
        // Strategy 3: Search all script tags for variant data
        if (variants.length === 0) {
          debugLog('[VariantImageHover] Searching for variant data script...');
          const allScripts = document.querySelectorAll('script[type="application/json"]');
          for (const script of allScripts) {
            try {
              const data = JSON.parse(script.textContent);
              if (Array.isArray(data) && data.length > 0 && data[0].id && data[0].option1 !== undefined) {
                variants = data;
                debugLog(`[VariantImageHover] Found ${variants.length} variants from script tag`);
                break;
              }
            } catch (e) {
              // Not variant data
            }
          }
        }
        
        if (variants.length === 0) {
          debugWarn('[VariantImageHover] No variants found');
          return;
        }

        // Get all media elements - try multiple selectors
        // First try gallery slider (if it exists)
        let mediaItems = document.querySelectorAll('.gallery-slider .product__media-item img');
        if (mediaItems.length === 0) {
          mediaItems = document.querySelectorAll('.gallery-slider .swiper-slide img');
        }
        if (mediaItems.length === 0) {
          mediaItems = document.querySelectorAll('.swiper-container.gallery-slider img');
        }
        if (mediaItems.length === 0) {
          mediaItems = document.querySelectorAll('.gallery .swiper-slide img');
        }
        if (mediaItems.length === 0) {
          mediaItems = document.querySelectorAll('.gallery img');
        }
        // Fallback: single product image
        if (mediaItems.length === 0) {
          const singleImg = document.querySelector('.product__media-wrapper img');
          if (singleImg) {
            mediaItems = [singleImg];
            debugLog('[VariantImageHover] Using single product image');
          }
        }
        
        // Collect all image URLs from variants (they have featured_image URLs)
        // This gives us access to actual image URLs without needing to fetch
        const allVariantImageUrls = new Set();
        variants.forEach(variant => {
          const imgUrl = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
          if (imgUrl) {
            allVariantImageUrls.add(imgUrl);
          }
        });
        
        debugLog(`[VariantImageHover] Found ${allVariantImageUrls.size} unique variant images`);
        
        // Try to fetch product JSON for all media (optional, may fail)
        const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0] || 
                             window.location.pathname.split('/products/')[1]?.split('/')[0];
        
        if (productHandle) {
          const productJsonUrl = `${window.location.origin}/products/${productHandle}.js`;
          debugLog(`[VariantImageHover] Attempting to fetch all product media from: ${productJsonUrl}`);
          
          return fetch(productJsonUrl)
            .then(res => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.json();
            })
            .then(productData => {
              debugLog(`[VariantImageHover] Product data keys:`, Object.keys(productData));
              
              // Try different image sources - collect ALL images with their data
              let allImages = [];
              let allImagesWithData = [];
              
              if (productData.media && Array.isArray(productData.media)) {
                // Media array has alt tags
                productData.media.forEach(m => {
                  const url = m.src || m.url || m.preview?.src || '';
                  const alt = m.alt || m.image?.altText || '';
                  if (url) {
                    allImages.push(url);
                    allImagesWithData.push({ url, alt });
                  }
                });
                debugLog(`[VariantImageHover] Found ${allImages.length} images in productData.media`);
              } else if (productData.images && Array.isArray(productData.images)) {
                productData.images.forEach(img => {
                  const url = typeof img === 'string' ? img : (img.src || img.url || '');
                  const alt = typeof img === 'object' ? (img.alt || img.altText || '') : '';
                  if (url) {
                    allImages.push(url);
                    allImagesWithData.push({ url, alt });
                  }
                });
                debugLog(`[VariantImageHover] Found ${allImages.length} images in productData.images`);
              }
              
              // Also collect from variants (they might have images not in main media)
              // Variants can have media arrays with multiple images
              if (productData.variants && Array.isArray(productData.variants)) {
                productData.variants.forEach(v => {
                  // Featured image
                  if (v.featured_image) {
                    const url = typeof v.featured_image === 'string' ? v.featured_image : (v.featured_image.src || v.featured_image.url || '');
                    const alt = typeof v.featured_image === 'object' ? (v.featured_image.alt || v.featured_image.altText || '') : '';
                    if (url && !allImages.includes(url)) {
                      allImages.push(url);
                      allImagesWithData.push({ url, alt });
                    }
                  }
                  // Featured media
                  if (v.featured_media?.preview_image) {
                    const url = v.featured_media.preview_image.src || '';
                    const alt = v.featured_media.alt || v.featured_media.preview_image.altText || '';
                    if (url && !allImages.includes(url)) {
                      allImages.push(url);
                      allImagesWithData.push({ url, alt });
                    }
                  }
                  // Variant media array (if it exists) - THIS IS KEY FOR BULLHORN FRONT IMAGES
                  if (v.media && Array.isArray(v.media)) {
                    v.media.forEach(m => {
                      const url = m.src || m.url || m.preview?.src || m.preview_image?.src || '';
                      const alt = m.alt || m.image?.altText || m.preview_image?.altText || '';
                      if (url && !allImages.includes(url)) {
                        allImages.push(url);
                        allImagesWithData.push({ url, alt });
                      }
                    });
                  }
                });
                debugLog(`[VariantImageHover] Found ${allImages.length} total images including variants`);
                debugLog(`[VariantImageHover] Images with data: ${allImagesWithData.length}`);
              }
              
              // Store images with data for later use in buildVariantImageMapFromProductData
              productData._allImagesWithData = allImagesWithData;
              
              // Add variant images to the set
              allImages.forEach(img => {
                const url = typeof img === 'string' ? img : (img.src || img.url || '');
                if (url) allVariantImageUrls.add(url);
              });
              
              // Build map from product data
              this.buildVariantImageMapFromProductData(productData, variants);
              this.updateButtonVisibility();
              debugLog(`[VariantImageHover] Map built from product JSON (size: ${Object.keys(this.variantImageMap).length})`);
            })
            .catch(e => {
              debugWarn('[VariantImageHover] Could not fetch product JSON (this is OK, using variant data):', e.message);
              // Use variant images we already have
              this.buildVariantImageMapFromProductData({ images: Array.from(allVariantImageUrls) }, variants);
              this.updateButtonVisibility();
            });
        } else {
          // Use variant images we already have
          this.buildVariantImageMapFromProductData({ images: Array.from(allVariantImageUrls) }, variants);
          this.updateButtonVisibility();
          return Promise.resolve();
        }
      }
      
      buildVariantImageMapFromProductData(productData, variants) {
        // Build mapping using alt tags instead of filenames
        // Alt tag format: "{Color} {Handlebar} {View}"
        // Example: "Green Flat Bar Front" (hover view), "Black Bullhorn Sideways" (default/featured view)
        // Note: Default view = Sideways alt tag, Hover view = Front alt tag
        
        // Get images with alt tags from DOM or product data
        let allImagesWithAlt = [];
        
        // Strategy 1: Get from DOM img elements (they have alt attributes) - MOST RELIABLE
        const domImages = document.querySelectorAll('.product__media img, .gallery-slider img, .swiper-slide img, .gallery img, .product__media-item img, img[alt]');
        domImages.forEach(img => {
          const src = img.src || img.currentSrc || '';
          const alt = img.alt || img.getAttribute('alt') || '';
          if (src && alt && alt.trim() !== '') {
            // Normalize URL - remove query params
            const cleanUrl = src.split('?')[0];
            allImagesWithAlt.push({ url: cleanUrl, alt: alt.trim() });
          }
        });
        
        debugLog(`[VariantImageHover] Found ${domImages.length} DOM images, ${allImagesWithAlt.length} with alt tags`);
        
        // Debug: Log summary of collected images
        debugLog(`[VariantImageHover] Collected ${allImagesWithAlt.length} images from DOM`);
        
        // Strategy 2: Get from product JSON if available (add to existing, don't replace DOM data)
        // First try productData.media (has alt tags)
        if (productData.media && Array.isArray(productData.media)) {
          productData.media.forEach(m => {
            const url = m.src || m.url || m.preview?.src || '';
            const alt = m.alt || m.image?.altText || '';
            if (url && alt) {
              // Normalize protocol-relative URLs and remove query params
              const normalizedUrl = (url.startsWith('//') ? `https:${url}` : url).split('?')[0];
              // Only add if we don't already have this URL (DOM takes precedence)
              const exists = allImagesWithAlt.some(img => {
                const imgUrl = img.url.split('?')[0];
                const imgFilename = imgUrl.split('/').pop() || '';
                const normalizedFilename = normalizedUrl.split('/').pop() || '';
                return imgUrl === normalizedUrl || imgFilename === normalizedFilename;
              });
              if (!exists) {
                allImagesWithAlt.push({ url: normalizedUrl, alt: alt.trim() });
              }
            }
          });
        }
        
        // Also check the stored images with data from fetch (includes variant media)
        if (productData._allImagesWithData && Array.isArray(productData._allImagesWithData)) {
          debugLog(`[VariantImageHover] Adding ${productData._allImagesWithData.length} images from product JSON (including variants)`);
          let addedCount = 0;
          productData._allImagesWithData.forEach(imgData => {
            const url = imgData.url || '';
            const alt = imgData.alt || '';
            if (url && alt && alt.trim() !== '') {
              const normalizedUrl = (url.startsWith('//') ? `https:${url}` : url).split('?')[0];
              const exists = allImagesWithAlt.some(img => {
                const imgUrl = img.url.split('?')[0];
                const imgFilename = imgUrl.split('/').pop() || '';
                const normalizedFilename = normalizedUrl.split('/').pop() || '';
                return imgUrl === normalizedUrl || imgFilename === normalizedFilename;
              });
              if (!exists) {
                allImagesWithAlt.push({ url: normalizedUrl, alt: alt.trim() });
                addedCount++;
              }
            }
          });
          debugLog(`[VariantImageHover] Added ${addedCount} new images from JSON`);
        }
        
        // Note: productData.images is redundant - already covered by DOM and productData.media/_allImagesWithData
        
        // Remove duplicates - match by URL or filename (domains might differ)
        const uniqueImages = new Map();
        const filenameMap = new Map(); // Track by filename for domain-agnostic matching
        
        allImagesWithAlt.forEach(img => {
          const cleanUrl = img.url.split('?')[0];
          const filename = cleanUrl.split('/').pop() || '';
          
          // Use URL as primary key
          if (!uniqueImages.has(cleanUrl)) {
            uniqueImages.set(cleanUrl, { url: cleanUrl, alt: img.alt });
          } else if (!uniqueImages.get(cleanUrl).alt && img.alt) {
            // Update if we have a better alt tag
            uniqueImages.set(cleanUrl, { url: cleanUrl, alt: img.alt });
          }
          
          // Also track by filename for domain-agnostic matching
          if (filename && !filenameMap.has(filename) || (filenameMap.has(filename) && !filenameMap.get(filename).alt && img.alt)) {
            filenameMap.set(filename, { url: cleanUrl, alt: img.alt });
          }
        });
        
        // Merge filename-based matches (for cases where domains differ)
        filenameMap.forEach((img, filename) => {
          const urlExists = Array.from(uniqueImages.values()).some(i => {
            const iFilename = i.url.split('/').pop() || '';
            return iFilename === filename;
          });
          if (!urlExists) {
            uniqueImages.set(img.url, img);
          }
        });
        
        allImagesWithAlt = Array.from(uniqueImages.values());
        
        debugLog(`[VariantImageHover] Rebuilding map with ${allImagesWithAlt.length} images (using alt tags)`);
        
        // Log all images with their alt tags (grouped by type for easier debugging)
        debugLog(`[VariantImageHover] Available images with alt tags:`);
        const bullhornFront = allImagesWithAlt.filter(img => (img.alt || '').toLowerCase().includes('bullhorn') && (img.alt || '').toLowerCase().includes('front'));
        const bullhornSideways = allImagesWithAlt.filter(img => (img.alt || '').toLowerCase().includes('bullhorn') && (img.alt || '').toLowerCase().includes('sideways'));
        const flatBarFront = allImagesWithAlt.filter(img => (img.alt || '').toLowerCase().includes('flat bar') && (img.alt || '').toLowerCase().includes('front'));
        const flatBarSideways = allImagesWithAlt.filter(img => (img.alt || '').toLowerCase().includes('flat bar') && (img.alt || '').toLowerCase().includes('sideways'));
        
        if (bullhornFront.length > 0) {
          debugLog(`  Bullhorn Front (${bullhornFront.length}):`);
          bullhornFront.forEach(img => {
            const filename = img.url.split('/').pop()?.split('?')[0] || 'unknown';
            debugLog(`    - ${filename}: "${img.alt}"`);
          });
        }
        if (bullhornSideways.length > 0) {
          debugLog(`  Bullhorn Sideways (${bullhornSideways.length}):`);
          bullhornSideways.forEach(img => {
            const filename = img.url.split('/').pop()?.split('?')[0] || 'unknown';
            debugLog(`    - ${filename}: "${img.alt}"`);
          });
        }
        if (flatBarFront.length > 0) {
          debugLog(`  Flat Bar Front (${flatBarFront.length}):`);
          flatBarFront.forEach(img => {
            const filename = img.url.split('/').pop()?.split('?')[0] || 'unknown';
            debugLog(`    - ${filename}: "${img.alt}"`);
          });
        }
        if (flatBarSideways.length > 0) {
          debugLog(`  Flat Bar Sideways (${flatBarSideways.length}):`);
          flatBarSideways.forEach(img => {
            const filename = img.url.split('/').pop()?.split('?')[0] || 'unknown';
            debugLog(`    - ${filename}: "${img.alt}"`);
          });
        }
        
        // Log any images without expected alt tags
        const otherImages = allImagesWithAlt.filter(img => {
          const alt = (img.alt || '').toLowerCase();
          return !alt.includes('bullhorn') && !alt.includes('flat bar');
        });
        if (otherImages.length > 0) {
          debugLog(`  Other images (${otherImages.length}):`);
          otherImages.forEach(img => {
            const filename = img.url.split('/').pop()?.split('?')[0] || 'unknown';
            debugLog(`    - ${filename}: "${img.alt || '(no alt)'}"`);
          });
        }
        
        variants.forEach((variant) => {
          if (!variant.featured_image && !variant.featured_media) return;
          
          const variantId = variant.id.toString();
          const variantColor = (variant.option1 || '').toLowerCase();
          const variantHandlebar = (variant.option3 || '').toLowerCase();
          const isBullhorn = variantHandlebar.includes('bullhorn') || variantHandlebar.includes('bull');
          
          // Get featured image URL and normalize it
          let featuredImageSrc = variant.featured_image?.src || variant.featured_media?.preview_image?.src || '';
          if (featuredImageSrc.startsWith('//')) {
            featuredImageSrc = `https:${featuredImageSrc}`;
          }
          const featuredUrl = featuredImageSrc.split('?')[0];
          
          // Extract filename from URL for matching (domain might differ)
          const featuredFilename = featuredUrl.split('/').pop()?.split('?')[0] || '';
          
          // Find featured image's alt tag - helper function to avoid duplication
          const findAltTag = (url, filename) => {
            // Try exact URL match first
            for (const img of allImagesWithAlt) {
              if (img.url.split('?')[0] === url) {
                return (img.alt || '').trim().toLowerCase();
              }
            }
            // Try filename match (handles domain differences)
            if (filename) {
              for (const img of allImagesWithAlt) {
                const imgFilename = img.url.split('/').pop()?.split('?')[0] || '';
                if (imgFilename === filename) {
                  return (img.alt || '').trim().toLowerCase();
                }
              }
            }
            // Try variant data
            if (variant.featured_image?.alt) return (variant.featured_image.alt || '').trim().toLowerCase();
            if (variant.featured_media?.alt) return (variant.featured_media.alt || '').trim().toLowerCase();
            return '';
          };
          
          const featuredAltTag = findAltTag(featuredUrl, featuredFilename);
          
          const colorCapitalized = variantColor.charAt(0).toUpperCase() + variantColor.slice(1);
          const handlebarName = isBullhorn ? 'Bullhorn' : 'Flat Bar';
          const expectedFrontAlt = `${colorCapitalized} ${handlebarName} Front`.toLowerCase();
          const expectedSidewaysAlt = `${colorCapitalized} ${handlebarName} Sideways`.toLowerCase();
          
          // CRITICAL: Featured images SHOULD ALWAYS be Sideways
          // If featured is Front, that's wrong - we need to find the Sideways image
          
          const featuredIsFront = featuredAltTag === expectedFrontAlt || (featuredAltTag.includes('front') && !featuredAltTag.includes('sideways'));
          const featuredIsSideways = featuredAltTag === expectedSidewaysAlt || (featuredAltTag.includes('sideways') && !featuredAltTag.includes('front'));
          
          debugLog(`[VariantImageHover] Variant ${variantId} (${variantColor}, ${handlebarName}):`);
          debugLog(`  Featured URL: ${featuredUrl}, Alt: "${featuredAltTag || 'unknown'}"`);
          debugLog(`  Featured is Front: ${featuredIsFront}, Featured is Sideways: ${featuredIsSideways}`);
          
          if (featuredIsFront) {
            debugWarn(`  ⚠️ Featured image is Front, but it SHOULD be Sideways! Searching for sideways...`);
          }
          
          let hoverImageUrl = null;
          let defaultImageUrl = null;
          
          // Step 1: ALWAYS find the Sideways image for default (featured should be sideways)
          // CRITICAL: For Bullhorn variants, NEVER use Flat Bar images - only use Bullhorn images
          
          // First, try to find the correct sideways image
          debugLog(`  Searching for ${handlebarName} sideways image (alt: "${expectedSidewaysAlt}")...`);
          
          for (const img of allImagesWithAlt) {
            const imgUrl = img.url.split('?')[0];
            const normalizedAlt = (img.alt || '').trim().toLowerCase();
            
            // Must match exact expected alt (includes handlebar type)
            if (normalizedAlt === expectedSidewaysAlt) {
              defaultImageUrl = imgUrl;
              debugLog(`  ✅ Found ${handlebarName} sideways image: ${imgUrl}`);
              break;
            }
          }
          
          // If we found sideways, use it (even if featured was front)
          if (defaultImageUrl) {
            if (featuredIsFront) {
              debugLog(`  ✅ Using found sideways as default (featured was incorrectly set to Front)`);
            } else if (featuredIsSideways && defaultImageUrl === featuredUrl) {
              debugLog(`  ✅ Featured is correctly sideways, using as default`);
            }
          } else {
            // No sideways found - this is a problem
            debugWarn(`  ⚠️ No ${handlebarName} sideways image found!`);
            debugWarn(`     Expected alt: "${expectedSidewaysAlt}"`);
            
            // For Bullhorn: NEVER fallback to Flat Bar - use Front if no sideways exists
            if (isBullhorn) {
              debugWarn(`  ⚠️ Bullhorn variant missing sideways image - will use Front as default`);
              // We'll set defaultImageUrl below to featured (which is Front)
            }
            
            // Use featured as fallback (even if it's Front - better than nothing)
            defaultImageUrl = featuredUrl;
            if (featuredIsFront) {
              debugWarn(`  ⚠️ Using Front image as default (no sideways available - this should be fixed in Shopify)`);
            } else {
              debugLog(`  Using featured as default: ${featuredUrl}`);
            }
          }
          
          // Step 2: Find the Front image for hover (ALWAYS search for it)
          // CRITICAL: Must match the CORRECT handlebar type (Bullhorn or Flat Bar)
          debugLog(`  Searching for ${handlebarName} front image with alt: "${expectedFrontAlt}"`);
          debugLog(`  Total images to search: ${allImagesWithAlt.length}`);
          
          // Debug: Show all available alt tags for this variant's handlebar type
          const relevantAlts = allImagesWithAlt
            .filter(img => {
              const alt = (img.alt || '').toLowerCase();
              const matchesColor = alt.includes(variantColor.toLowerCase());
              const matchesHandlebar = isBullhorn 
                ? alt.includes('bullhorn') 
                : (alt.includes('flat bar') || alt.includes('flatbar'));
              return matchesColor && matchesHandlebar;
            })
            .map(img => `"${img.alt}"`);
          if (relevantAlts.length > 0) {
            debugLog(`  Relevant ${handlebarName} alt tags found: ${relevantAlts.join(', ')}`);
          }
          
          for (const img of allImagesWithAlt) {
            const imgUrl = img.url.split('?')[0];
            const normalizedAlt = (img.alt || '').trim().toLowerCase();
            const filename = imgUrl.split('/').pop() || '';
            
            // Try exact match first
            if (normalizedAlt === expectedFrontAlt) {
              hoverImageUrl = imgUrl;
              debugLog(`  ✅ Found ${handlebarName} front image for hover: ${imgUrl}`);
              break;
            }
            
            // Flexible match: check if alt contains all required parts (color + handlebar + front)
            if (!hoverImageUrl) {
              const matchesColor = normalizedAlt.includes(variantColor.toLowerCase());
              const matchesHandlebar = isBullhorn 
                ? normalizedAlt.includes('bullhorn') 
                : (normalizedAlt.includes('flat bar') || normalizedAlt.includes('flatbar'));
              const matchesFront = normalizedAlt.includes('front') && !normalizedAlt.includes('sideways');
              
              if (matchesColor && matchesHandlebar && matchesFront) {
                hoverImageUrl = imgUrl;
                debugLog(`  ✅ Found ${handlebarName} front image for hover (flexible match): ${imgUrl}`);
                break;
              }
            }
          }
          
          // If featured is Front and we didn't find it in search, use featured
          // But ONLY if it matches the handlebar type
          if (!hoverImageUrl && featuredIsFront) {
            const featuredAltLower = featuredAltTag.toLowerCase();
            const featuredMatchesHandlebar = isBullhorn 
              ? featuredAltLower.includes('bullhorn')
              : (featuredAltLower.includes('flat bar') || featuredAltLower.includes('flatbar'));
            
            if (featuredMatchesHandlebar) {
              hoverImageUrl = featuredUrl;
              debugLog(`  ✅ Using featured image as hover (it's ${handlebarName} front): ${featuredUrl}`);
            } else {
              debugWarn(`  ⚠️ Featured is Front but wrong handlebar type - not using as hover`);
            }
          }
          
          if (!hoverImageUrl) {
            debugWarn(`  ⚠️ No front image found for hover`);
            debugWarn(`     Expected: "${expectedFrontAlt}"`);
            debugWarn(`     All available alt tags:`, allImagesWithAlt.map(img => `"${img.alt}"`).filter(Boolean).join(', '));
          }
          
          // Ensure we have a default image
          if (!defaultImageUrl) {
            defaultImageUrl = featuredUrl;
            debugWarn(`  ⚠️ No default image found, using featured: ${featuredUrl}`);
          }
          
          // Log final mapping
          debugLog(`  Final mapping: Default=${defaultImageUrl}, Hover=${hoverImageUrl || 'NONE'}`);
          
          
          // Create mapping: Only if we have a hover (front) image that's different from default
          // Default should always be Sideways, Hover should always be Front
          // IMPORTANT: Don't create mapping if they're the same (no hover effect possible)
          if (hoverImageUrl && defaultImageUrl && hoverImageUrl !== defaultImageUrl) {
            // Verify: default should be sideways, hover should be front
            // Handle domain differences by matching filename
            const defaultFilename = defaultImageUrl.split('/').pop()?.split('?')[0] || '';
            const hoverFilename = hoverImageUrl.split('/').pop()?.split('?')[0] || '';
            
            // Find alt tags using helper function (reuses same logic as featured alt lookup)
            const findAltTag = (url, filename) => {
              // Try exact URL match first
              for (const img of allImagesWithAlt) {
                if (img.url.split('?')[0] === url) {
                  return (img.alt || '').trim().toLowerCase();
                }
              }
              // Try filename match (handles domain differences)
              if (filename) {
                for (const img of allImagesWithAlt) {
                  const imgFilename = img.url.split('/').pop()?.split('?')[0] || '';
                  if (imgFilename === filename && img.alt) {
                    return (img.alt || '').trim().toLowerCase();
                  }
                }
              }
              return '';
            };
            
            const defaultAlt = findAltTag(defaultImageUrl, defaultFilename);
            const hoverAlt = findAltTag(hoverImageUrl, hoverFilename);
            
            // Validate image types - trust search results if alt tags not found (domain mismatch)
            const defaultIsSideways = !defaultAlt || (defaultAlt.includes('sideways') && !defaultAlt.includes('front'));
            const hoverIsFront = !hoverAlt || (hoverAlt.includes('front') && !hoverAlt.includes('sideways'));
            
            if (!defaultIsSideways || !hoverIsFront) {
              debugWarn(`  ⚠️ Skipping mapping - image types don't match:`);
              debugWarn(`     Default: ${defaultFilename} - Alt: "${defaultAlt || 'not found'}" (should be sideways)`);
              debugWarn(`     Hover: ${hoverFilename} - Alt: "${hoverAlt || 'not found'}" (should be front)`);
            } else {
              // Create the mapping
              this.variantImageMap[variantId] = hoverImageUrl;
              this.variantImageMap[`${variantId}_original`] = defaultImageUrl;
              
              // Determine type for logging
              const defaultType = defaultAlt 
                ? (defaultAlt.includes('bullhorn') ? 'Bullhorn Sideways' : 'Flat Bar Sideways')
                : (expectedSidewaysAlt.includes('bullhorn') ? 'Bullhorn Sideways' : 'Flat Bar Sideways');
              
              debugLog(`  ✅ Mapped: Default=${defaultFilename} (${defaultType}), Hover=${hoverFilename} (Front)`);
            }
          } else {
            if (!hoverImageUrl) {
              debugWarn(`  ⚠️ No hover image found for variant ${variantId}`);
              debugWarn(`     Expected Front Alt: "${expectedFrontAlt}"`);
              debugWarn(`     Available images:`, allImagesWithAlt.map(img => `"${img.alt}"`).filter(Boolean).join(', '));
            }
            if (!defaultImageUrl) {
              debugWarn(`  ⚠️ No default image found for variant ${variantId}`);
            }
            if (hoverImageUrl === defaultImageUrl) {
              debugWarn(`  ⚠️ Hover and default are the same (no hover effect possible): ${hoverImageUrl?.split('/').pop()}`);
              debugWarn(`     This variant may be missing a sideways image`);
            }
          }
        });
        
        debugLog(`[VariantImageHover] Rebuilt mapping: ${Object.keys(this.variantImageMap).length} variants have front images (hover view)`);
        debugLog(`[VariantImageHover] Note: Default view = Sideways alt tag, Hover view = Front alt tag`);
      }

      findMainImage() {
        // Find the active/visible main image - try multiple selectors
        let activeSlide = null;
        
        // Strategy 1: Gallery slider (if exists)
        let gallerySlider = document.querySelector('.swiper-container.gallery-slider');
        if (gallerySlider) {
          activeSlide = gallerySlider.querySelector('.swiper-slide.active img') ||
                       gallerySlider.querySelector('.swiper-slide.swiper-slide-active img') ||
                       gallerySlider.querySelector('.product__media-item.is-active img');
          
          if (!activeSlide) {
            const slides = gallerySlider.querySelectorAll('.swiper-slide');
            for (const slide of slides) {
              const img = slide.querySelector('img');
              if (img && slide.offsetParent !== null) {
                activeSlide = img;
                break;
              }
            }
          }
          
          if (!activeSlide) {
            activeSlide = gallerySlider.querySelector('img');
          }
        }
        
        // Strategy 2: Single product image (fallback)
        if (!activeSlide) {
          activeSlide = document.querySelector('.product__media-wrapper img');
        }
        
        // Strategy 3: Any product image
        if (!activeSlide) {
          activeSlide = document.querySelector('.product__media-item img');
        }
        
        if (!activeSlide) {
          debugWarn('[VariantImageHover] Main image not found');
          return false;
        }
        
        debugLog('[VariantImageHover] Found main image:', activeSlide.src.split('/').pop());

        if (activeSlide && activeSlide !== this.mainImageElement) {
          // If we were hovering or toggled, restore original before switching
          if ((this.isHovering || this.isToggled) && this.mainImageElement && this.originalImageSrc) {
            this.mainImageElement.src = this.originalImageSrc;
            this.isHovering = false;
            this.isToggled = false;
          }

          this.mainImageElement = activeSlide;
          this.originalImageSrc = activeSlide.src || activeSlide.currentSrc;
          debugLog(`[VariantImageHover] Found main image: ${this.originalImageSrc.split('/').pop()}`);
          return true;
        }

        return false;
      }

      listenToVariantChanges() {
        // Listen for variant change events from VariantSelects
        document.addEventListener('variant:change', (event) => {
          if (event.detail && event.detail.variantId) {
            debugLog(`[VariantImageHover] Variant change event: ${event.detail.variantId}`);
            this.updateCurrentVariant(event.detail.variantId);
            // Re-find main image as it may have changed
            this.findMainImage();
            this.setupHoverHandlers();
            // Ensure default image is sideways after variant change (only if not hovering/toggled)
            if (!this.isHovering && !this.isToggled) {
              this.ensureSidewaysView();
            }
          }
        });

        // Listen to variant changes from multiple sources
        // 1. variant-selects element
        let variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) {
          variantSelects = document.querySelector('variant-radios');
        }
        if (!variantSelects) {
          variantSelects = document.querySelector('variant-selects[data-section]');
        }
        if (!variantSelects) {
          variantSelects = document.querySelector('variant-radios[data-section]');
        }
        if (variantSelects) {
          variantSelects.addEventListener('change', () => {
            const variantInput = document.querySelector('input[name="id"]');
            if (variantInput && variantInput.value) {
              debugLog(`[VariantImageHover] Variant select changed: ${variantInput.value}`);
              this.updateCurrentVariant(variantInput.value);
              // Rebuild map and update handlers
              this.buildVariantImageMap().then(() => {
                this.findMainImage();
                this.setupHoverHandlers();
                // Only ensure sideways if not hovering/toggled
                if (!this.isHovering && !this.isToggled) {
                  this.ensureSidewaysView();
                }
              });
            }
          });
        }
        
        // 2. Product bundle variant selectors
        const bundleSelects = document.querySelectorAll('.bundle-product-options input[type="radio"].single-option-selector');
        bundleSelects.forEach(select => {
          select.addEventListener('change', () => {
            // Get selected variant from data-variant attribute or calculate from selected options
            const productId = document.querySelector('[data-product-id]')?.dataset?.productId;
            if (productId && window.productVariants && window.productVariants[productId]) {
              const variants = window.productVariants[productId];
              // Find variant matching current selections
              const colorInput = document.querySelector(`input[name="option-0-${productId}"]:checked`);
              const sizeInput = document.querySelector(`input[name="option-1-${productId}"]:checked`);
              const handlebarInput = document.querySelector(`input[name="option-2-${productId}"]:checked`);
              
              if (colorInput && sizeInput && handlebarInput) {
                const variant = variants.find(v => 
                  v.option1 === colorInput.value &&
                  v.option2 === sizeInput.value &&
                  v.option3 === handlebarInput.value
                );
                
                if (variant) {
                  debugLog(`[VariantImageHover] Bundle variant changed: ${variant.id}`);
                  this.updateCurrentVariant(variant.id.toString());
                  // Rebuild map and update handlers
                  this.buildVariantImageMap().then(() => {
                    this.findMainImage();
                    this.setupHoverHandlers();
                    // Only ensure sideways if not hovering/toggled
                    if (!this.isHovering && !this.isToggled) {
                      this.ensureSidewaysView();
                    }
                  });
                }
              }
            }
          });
        });
        
        // Listen for media gallery slide changes
        const gallerySlider = document.querySelector('.gallery-slider');
        if (gallerySlider) {
          // Use MutationObserver to detect when active slide changes
          const observer = new MutationObserver(() => {
            // Skip if hovering or toggled (user is intentionally viewing front)
            if (this.isHovering || this.isToggled) {
              return;
            }
            
            // Re-find main image when gallery updates
            const oldImage = this.mainImageElement;
            const imageChanged = this.findMainImage();
            if (imageChanged && oldImage !== this.mainImageElement) {
              // Image changed, update handlers
              this.setupHoverHandlers();
              // Ensure default image is sideways when gallery slide changes
              this.ensureSidewaysView();
            }
          });

          observer.observe(gallerySlider, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
          });

          // Also listen for Swiper slide change events if Swiper is used
          if (gallerySlider.swiper) {
            gallerySlider.swiper.on('slideChange', () => {
              // Skip if hovering or toggled (user is intentionally viewing front)
              if (this.isHovering || this.isToggled) {
                return;
              }
              
              const oldImage = this.mainImageElement;
              const imageChanged = this.findMainImage();
              if (imageChanged && oldImage !== this.mainImageElement) {
                this.setupHoverHandlers();
                // Ensure default image is sideways when swiper slide changes
                this.ensureSidewaysView();
              }
            });
          }
        }

      }

      setInitialVariant() {
        // Initial variant detection - try multiple sources
        // 1. URL params (Shopify standard 'variant' first, then fallback)
        const urlParams = new URLSearchParams(window.location.search);
        let variantId = urlParams.get('variant') || urlParams.get('variantId');
        
        debugLog('[VariantImageHover] Detecting initial variant...');
        debugLog('[VariantImageHover] URL variant:', variantId);
        
        // 2. Hidden input
        if (!variantId) {
          const variantInput = document.querySelector('input[name="id"]');
          if (variantInput && variantInput.value) {
            variantId = variantInput.value;
            debugLog('[VariantImageHover] Found variant from input:', variantId);
          }
        }
        
        // 3. Product bundle selected variant
        if (!variantId) {
          const productId = document.querySelector('[data-product-id]')?.dataset?.productId;
          if (productId && window.productVariants && window.productVariants[productId]) {
            const variants = window.productVariants[productId];
            const colorInput = document.querySelector(`input[name="option-0-${productId}"]:checked`);
            const sizeInput = document.querySelector(`input[name="option-1-${productId}"]:checked`);
            const handlebarInput = document.querySelector(`input[name="option-2-${productId}"]:checked`);
            
            if (colorInput && sizeInput && handlebarInput) {
              const variant = variants.find(v => 
                v.option1 === colorInput.value &&
                v.option2 === sizeInput.value &&
                v.option3 === handlebarInput.value
              );
              if (variant) {
                variantId = variant.id.toString();
                debugLog('[VariantImageHover] Found variant from bundle:', variantId);
              }
            }
          }
        }
        
        if (variantId) {
          debugLog(`[VariantImageHover] ✅ Initial variant detected: ${variantId}`);
          this.updateCurrentVariant(variantId);
        } else {
          debugWarn('[VariantImageHover] ⚠️ No initial variant found, will detect on first interaction');
        }
      }

      updateCurrentVariant(variantId) {
        if (!variantId) {
          debugWarn('[VariantImageHover] updateCurrentVariant called without variantId');
          return;
        }
        
        const previousVariantId = this.currentVariantId;
        this.currentVariantId = variantId;
        
        // If variant changed and we were toggled, restore original first
        if (previousVariantId && previousVariantId !== variantId && this.isToggled && this.mainImageElement && this.originalImageSrc) {
          this.mainImageElement.src = this.originalImageSrc;
          debugLog(`[VariantImageHover] Variant changed, restored original image before switching`);
        }
        
        // Reset toggle and hover state when variant changes
        this.isToggled = false;
        this.isHovering = false;
        
        // Update hover image for current variant
        if (this.variantImageMap[variantId]) {
          this.hoverImageSrc = this.variantImageMap[variantId];
          debugLog(`[VariantImageHover] Variant ${variantId} → Hover image: ${this.hoverImageSrc.split('/').pop()}`);
        } else {
          this.hoverImageSrc = null;
          debugWarn(`[VariantImageHover] No hover image found for variant ${variantId}`);
        }

        // Update button visibility
        this.updateButtonVisibility();
        
        // Reset button text and state
        if (this.viewToggleButton) {
          const textElement = this.viewToggleButton.querySelector('.variant-view-toggle__text');
          if (textElement) {
            textElement.textContent = 'View Front';
          }
          this.viewToggleButton.classList.remove('is-active');
        }

        // IMPORTANT: Re-find main image and update originalImageSrc to current variant's featured image
        // This ensures we always restore to the correct variant's image
        setTimeout(() => {
          this.findMainImage();
          if (this.mainImageElement) {
            // Check if we have a stored "original" (sideways) image for this variant
            // This happens when the featured image IS the front image (hover view)
            const originalImageKey = `${variantId}_original`;
            debugLog(`[VariantImageHover] Checking for stored original image with key: ${originalImageKey}`);
            debugLog(`[VariantImageHover] Available keys in variantImageMap:`, Object.keys(this.variantImageMap).filter(k => k.includes(variantId)));
            
            if (this.variantImageMap[originalImageKey]) {
              // Use the stored sideways image as the original
              this.originalImageSrc = this.variantImageMap[originalImageKey];
              debugLog(`[VariantImageHover] ✅ Using stored sideways image as original for variant ${variantId}: ${this.originalImageSrc.split('/').pop()}`);
              
              // Only update if we're not hovering or toggled (to avoid conflicts)
              if (!this.isHovering && !this.isToggled) {
                // Set flag to prevent watchForImageChanges from interfering
                this._isChangingImage = true;
                this.mainImageElement.src = this.originalImageSrc;
                debugLog(`[VariantImageHover] ✅ Updated main image to sideways view (default): ${this.originalImageSrc.split('/').pop()}`);
                setTimeout(() => {
                  this._isChangingImage = false;
                }, 100);
              }
            } else {
              // Normal case: featured image is sideways, use it as original
              this.originalImageSrc = this.mainImageElement.src || this.mainImageElement.currentSrc;
              debugLog(`[VariantImageHover] Updated original image for variant ${variantId}: ${this.originalImageSrc.split('/').pop()}`);
            }
          }
        });
      }

      setupHoverHandlers() {
        if (!this.mainImageElement) {
          // Retry finding the image after a short delay
          setTimeout(() => {
            this.findMainImage();
            if (this.mainImageElement) {
              this.setupHoverHandlers();
            }
          }, 500);
          return;
        }

        // CRITICAL: Ensure we have a variant and hover image before setting up handlers
        // If no variant is set yet, try to set initial variant first
        if (!this.currentVariantId) {
          debugLog('[VariantImageHover] No variant set, attempting to set initial variant...');
          this.setInitialVariant();
        }

        // If still no hover image, try to update current variant
        if (!this.hoverImageSrc && this.currentVariantId) {
          debugLog('[VariantImageHover] No hover image set, updating current variant...');
          this.updateCurrentVariant(this.currentVariantId);
        }

        // Remove existing handlers to avoid duplicates
        // Check if we already have handlers on this image
        if (this.mainImageElement._hasHoverHandlers) {
          const currentImage = this.mainImageElement;
          const newImage = document.querySelector('.product__media-wrapper img');
          
          // Only re-setup if image element changed
          if (newImage === currentImage) {
            return;
          }
        }

        // Get the container element for button placement
        const container = this.mainImageElement.closest('.swiper-slide') || 
                         this.mainImageElement.closest('.product__media-item') ||
                         this.mainImageElement.closest('.product__media-wrapper') ||
                         this.mainImageElement.parentElement;

        if (!container) {
          debugWarn('[VariantImageHover] Could not find container for main image');
          return;
        }

        // Store container reference for button placement
        this.hoverContainer = container;

        debugLog(`[VariantImageHover] Setting up hover handlers and toggle button`);

        // Create and insert toggle button (always create it, visibility will be updated)
        this.createViewToggleButton(container);
        
        // Ensure button visibility is updated after a short delay
        setTimeout(() => {
          this.updateButtonVisibility();
        }, 300);

        // Mark that handlers are attached to prevent duplicates
        this.mainImageElement._hasHoverHandlers = true;

        // Mouse enter - hover effect (attach directly to image, not container)
        this.mainImageElement.addEventListener('mouseenter', (e) => {
          // Ignore if button is currently hovered (simple flag check)
          if (this._buttonHovered) {
            return;
          }
          
          debugLog('[VariantImageHover] 🖱️ Mouse enter detected on image');
          
          // Always verify we have the correct variant and image before hovering
          if (!this.mainImageElement) {
            debugLog('[VariantImageHover]   Finding main image...');
            this.findMainImage();
          }
          
          // CRITICAL: Verify we have the correct variant's hover image
          // Re-check variant to ensure we're using the right image
          if (!this.currentVariantId) {
            debugLog('[VariantImageHover]   Setting initial variant...');
            this.setInitialVariant();
          }
          
          // Update hover image for current variant if not set
          if (this.currentVariantId && !this.hoverImageSrc) {
            debugLog('[VariantImageHover]   Updating current variant to get hover image...');
            this.updateCurrentVariant(this.currentVariantId);
          }
          
          debugLog('[VariantImageHover]   State check:');
          debugLog('[VariantImageHover]     hoverImageSrc:', this.hoverImageSrc ? `SET (${this.hoverImageSrc.split('/').pop()})` : 'NULL');
          debugLog('[VariantImageHover]     mainImageElement:', this.mainImageElement ? 'EXISTS' : 'NULL');
          debugLog('[VariantImageHover]     isToggled:', this.isToggled);
          debugLog('[VariantImageHover]     currentVariantId:', this.currentVariantId);
          debugLog('[VariantImageHover]     variantImageMap size:', Object.keys(this.variantImageMap).length);
          
          if (this.hoverImageSrc && this.mainImageElement && !this.isToggled) {
            // Verify this hover image belongs to the current variant
            const expectedVariantId = this.currentVariantId;
            const hoverImageForVariant = this.variantImageMap[expectedVariantId];
            
            if (hoverImageForVariant && hoverImageForVariant !== this.hoverImageSrc) {
              debugWarn(`[VariantImageHover] ⚠️ Hover image mismatch! Updating...`);
              this.hoverImageSrc = hoverImageForVariant;
            }
            
            // CRITICAL: Check if we have a stored sideways image for this variant first
            // This is the correct original/default view when featured image is Front
            const originalImageKey = `${this.currentVariantId}_original`;
            if (this.variantImageMap[originalImageKey]) {
              // Use stored sideways image as original
              this.originalImageSrc = this.variantImageMap[originalImageKey];
              debugLog(`[VariantImageHover] Using stored sideways image as original for hover: ${this.originalImageSrc.split('/').pop()}`);
            } else {
              // No stored sideways image - featured image should be sideways (normal case)
              let featuredImageSrc = null;
              
              // Try to get from variant data
              if (this.currentVariantId && window.productVariants) {
                const productId = document.querySelector('[data-product-id]')?.dataset?.productId;
                if (productId && window.productVariants[productId]) {
                  const variant = window.productVariants[productId].find(v => v.id.toString() === this.currentVariantId);
                  if (variant && variant.featured_image) {
                    featuredImageSrc = variant.featured_image.src || variant.featured_image;
                  }
                }
              }
              
              // Fallback: Use current image only if it's NOT the hover image
              if (!featuredImageSrc) {
                const currentImage = this.mainImageElement.src || this.mainImageElement.currentSrc;
                const currentUrl = currentImage.split('?')[0];
                const hoverUrl = this.hoverImageSrc.split('?')[0];
                
                // Only use current image if it's different from hover image (compare URLs, not filenames)
                if (currentUrl !== hoverUrl) {
                  featuredImageSrc = currentImage;
                }
              }
              
              // Store featured image as original (only if we don't have one or variant changed)
              if (featuredImageSrc) {
                const cleanFeaturedSrc = featuredImageSrc.split('?')[0];
                const currentOriginalUrl = this.originalImageSrc ? this.originalImageSrc.split('?')[0] : '';
                // Compare URLs, not filenames
                if (!this.originalImageSrc || currentOriginalUrl !== cleanFeaturedSrc) {
                  this.originalImageSrc = cleanFeaturedSrc;
                }
              } else {
                // Last resort: use current image
                const currentImage = this.mainImageElement.src || this.mainImageElement.currentSrc;
                if (!this.originalImageSrc) {
                  this.originalImageSrc = currentImage;
                }
              }
            }
            
            debugLog(`[VariantImageHover] Mouse enter - switching to hover image for variant ${this.currentVariantId}`);
            debugLog(`[VariantImageHover] Original: ${this.originalImageSrc.split('/').pop()}, Hover: ${this.hoverImageSrc.split('/').pop()}`);
            
            this.isHovering = true;
            this.switchToHoverImage();
          } else if (!this.hoverImageSrc) {
            debugWarn(`[VariantImageHover] ⚠️ No hover image available for variant ${this.currentVariantId} (this variant may not have a front image)`);
          }
        });

        // Mouse leave - restore original (only if not toggled)
        // Attach directly to image, not container
        this.mainImageElement.addEventListener('mouseleave', () => {
          if (this.isHovering && this.mainImageElement && !this.isToggled) {
            this.isHovering = false;
            this.restoreOriginalImage();
          }
        });

        // Touch devices: handle touchstart/touchend for hover-like effect
        // Attach directly to image, not container
        this.mainImageElement.addEventListener('touchstart', () => {
          if (this.hoverImageSrc && this.mainImageElement && !this.isToggled && !this.isHovering) {
            this.isHovering = true;
            if (!this.originalImageSrc) {
              this.originalImageSrc = this.mainImageElement.src || this.mainImageElement.currentSrc;
            }
            this.switchToHoverImage();
          }
        });

        this.mainImageElement.addEventListener('touchend', () => {
          if (this.isHovering && this.mainImageElement && !this.isToggled) {
            // Delay restore to allow user to see the image
            setTimeout(() => {
              if (this.isHovering && !this.isToggled) {
                this.isHovering = false;
                this.restoreOriginalImage();
              }
            }, 1000);
          }
        });
      }

      switchToHoverImage() {
        debugLog('[VariantImageHover] switchToHoverImage called');
        debugLog('[VariantImageHover]   mainImageElement:', this.mainImageElement ? 'EXISTS' : 'NULL');
        debugLog('[VariantImageHover]   hoverImageSrc:', this.hoverImageSrc || 'NULL');
        debugLog('[VariantImageHover]   currentVariantId:', this.currentVariantId || 'NULL');
        debugLog('[VariantImageHover]   variantImageMap keys:', Object.keys(this.variantImageMap).filter(k => k.includes(this.currentVariantId || '')).join(', ') || 'NONE');
        debugLog('[VariantImageHover]   isHovering:', this.isHovering);
        debugLog('[VariantImageHover]   isToggled:', this.isToggled);
        
        if (!this.mainImageElement) {
          debugWarn('[VariantImageHover] Cannot switch: missing mainImageElement');
          this.findMainImage();
          if (!this.mainImageElement) {
            debugError('[VariantImageHover] Still no mainImageElement after findMainImage()');
            return;
          }
        }
        
        if (!this.hoverImageSrc) {
          debugWarn('[VariantImageHover] Cannot switch: missing hoverImageSrc');
          if (this.currentVariantId && this.variantImageMap[this.currentVariantId]) {
            this.hoverImageSrc = this.variantImageMap[this.currentVariantId];
            debugLog('[VariantImageHover] Re-fetched hoverImageSrc from map:', this.hoverImageSrc);
          } else {
            debugError('[VariantImageHover] No hoverImageSrc in map for variant:', this.currentVariantId);
            debugError('[VariantImageHover] Available variants in map:', Object.keys(this.variantImageMap));
            return;
          }
        }

        debugLog(`[VariantImageHover] Switching to: ${this.hoverImageSrc.split('/').pop()}`);

        // Set flag to prevent watchForImageChanges from interfering
        this._isChangingImage = true;

        // Create a new image to preload
        const img = new Image();
        img.onload = () => {
          debugLog('[VariantImageHover] Hover image loaded successfully');
          // Only switch if we're still hovering or toggled (state might have changed)
          if (this.mainImageElement && (this.isHovering || this.isToggled)) {
            // Update the image src directly
            const oldSrc = this.mainImageElement.src;
            this.mainImageElement.src = this.hoverImageSrc;
            // Also update srcset if it exists
            if (this.mainImageElement.srcset) {
              // Keep srcset but ensure src is updated
              this.mainImageElement.src = this.hoverImageSrc;
            }
            debugLog(`[VariantImageHover] ✅ Image switched successfully`);
            debugLog(`[VariantImageHover]   Old: ${oldSrc.split('/').pop()}`);
            debugLog(`[VariantImageHover]   New: ${this.hoverImageSrc.split('/').pop()}`);
            
            // Clear flag after a short delay to allow mutation observer to settle
            setTimeout(() => {
              this._isChangingImage = false;
            }, 100);
          } else {
            debugLog(`[VariantImageHover] Image loaded but state changed (hovering: ${this.isHovering}, toggled: ${this.isToggled}), not switching`);
            this._isChangingImage = false;
          }
        };
        img.onerror = () => {
          debugWarn('[VariantImageHover] ❌ Failed to load hover image:', this.hoverImageSrc);
          debugWarn('[VariantImageHover] This usually means the image URL is incorrect or the image was deleted');
          this._isChangingImage = false;
        };
        img.src = this.hoverImageSrc;
      }
      
      restoreOriginalImage() {
        if (!this.mainImageElement) {
          this.findMainImage();
          if (!this.mainImageElement) return;
        }
        
        // Don't restore if button is toggled (button takes precedence)
        if (this.isToggled) {
          return;
        }
        
        // Set flag to prevent watchForImageChanges from interfering
        this._isChangingImage = true;
        
        // CRITICAL: Restore to the stored original image (current variant's featured image)
        // We stored this when hovering started, so use it
        if (this.originalImageSrc) {
          this.mainImageElement.src = this.originalImageSrc;
          debugLog(`[VariantImageHover] Restored to original image for variant ${this.currentVariantId}: ${this.originalImageSrc.split('/').pop()}`);
        } else {
          // Fallback: get current image if no original stored
          const currentImage = this.mainImageElement.src || this.mainImageElement.currentSrc;
          this.mainImageElement.src = currentImage;
          this.originalImageSrc = currentImage;
          debugLog(`[VariantImageHover] Restored using current image (no original stored): ${currentImage.split('/').pop()}`);
        }
        
        // Clear flag after a short delay
        setTimeout(() => {
          this._isChangingImage = false;
        }, 100);
      }

      createViewToggleButton(container) {
        // Remove existing button if present (clean up event listeners)
        if (this.viewToggleButton) {
          if (this.viewToggleButton._toggleHandler) {
            this.viewToggleButton.removeEventListener('click', this.viewToggleButton._toggleHandler);
          }
          this.viewToggleButton.remove();
          this.viewToggleButton = null;
        }

        // Create button element
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'variant-view-toggle';
        button.setAttribute('aria-label', 'Toggle side view');
        button.innerHTML = `
          <span class="variant-view-toggle__icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 2L2 7L10 12L18 7L10 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 12L10 17L18 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 7L10 12L18 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="variant-view-toggle__text">View Front</span>
        `;

        // Style the button - position at top-right, smaller and less intrusive
        button.style.cssText = `
          position: absolute;
          top: -30px;
          right: 10px;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.98);
          border: 1px solid rgba(0, 0, 0, 0.15);
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #000;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 100;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(8px);
          pointer-events: auto;
          max-width: 140px;
        `;
        
        // Add mobile-specific styling via media query
        // Only add style once (check if already exists)
        if (!document.getElementById('variant-view-toggle-mobile-styles')) {
          const style = document.createElement('style');
          style.id = 'variant-view-toggle-mobile-styles';
          style.textContent = `
            @media screen and (max-width: 749px) {
              .variant-view-toggle {
                right: 2px !important;
                padding: 5px 8px !important;
                font-size: 10px !important;
                max-width: 110px !important;
                gap: 4px !important;
              }
              .variant-view-toggle__icon svg {
                width: 16px !important;
                height: 16px !important;
              }
            }
          `;
          document.head.appendChild(style);
        }

        // Hover styles - set flag to prevent image hover
        button.addEventListener('mouseenter', (e) => {
          e.stopPropagation();
          // Set flag so image hover ignores this
          this._buttonHovered = true;
          button.style.background = 'rgba(255, 255, 255, 1)';
          button.style.borderColor = 'rgba(0, 0, 0, 0.2)';
          button.style.transform = 'translateY(-2px)';
          button.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        });

        button.addEventListener('mouseleave', (e) => {
          e.stopPropagation();
          // Clear flag
          this._buttonHovered = false;
          button.style.background = 'rgba(255, 255, 255, 0.95)';
          button.style.borderColor = 'rgba(0, 0, 0, 0.1)';
          button.style.transform = 'translateY(0)';
          button.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
        });

        // Toggle functionality - use arrow function to preserve 'this' context
        const toggleHandler = (e) => {
          e.stopPropagation();
          e.preventDefault();
          debugLog('[VariantImageHover] 🔘 Button clicked!');
          debugLog('[VariantImageHover] Current state:', {
            isToggled: this.isToggled,
            hoverImageSrc: this.hoverImageSrc ? 'exists' : 'null',
            mainImageElement: this.mainImageElement ? 'exists' : 'null',
            currentVariantId: this.currentVariantId
          });
          this.toggleView();
        };
        
        button.addEventListener('click', toggleHandler);
        
        // Store handler reference for potential cleanup (though we don't remove it)
        button._toggleHandler = toggleHandler;

        // Make container position relative if needed
        const containerStyle = window.getComputedStyle(container);
        if (containerStyle.position === 'static') {
          container.style.position = 'relative';
        }

        // Insert button into container
        container.appendChild(button);
        this.viewToggleButton = button;
        
        debugLog('[VariantImageHover] 🔘 Button created and inserted into container');

        // Always show button initially, update visibility later
        button.style.display = 'flex';
        
        // Update button visibility based on hover image availability
        // But don't hide it immediately - wait a bit for variant to be set
        setTimeout(() => {
          this.updateButtonVisibility();
        }, 500);
      }

      toggleView() {
        debugLog('[VariantImageHover] 🔄 toggleView() called');
        
        // Always ensure we have the main image element
        if (!this.mainImageElement) {
          debugWarn('[VariantImageHover] Cannot toggle: main image element not found, searching...');
          this.findMainImage();
          if (!this.mainImageElement) {
            debugError('[VariantImageHover] Cannot toggle: main image element still not found');
            // Try to re-setup handlers
            setTimeout(() => {
              this.setupHoverHandlers();
            }, 500);
            return;
          }
        }

        // Stop hovering when button is toggled (button takes precedence)
        this.isHovering = false;

        if (!this.isToggled) {
          // Switching TO front view (hover view)
          // Note: Default = Sideways alt tag, Hover = Front alt tag
          // First, ensure we have hover image for current variant
          if (!this.hoverImageSrc && this.currentVariantId) {
            this.hoverImageSrc = this.variantImageMap[this.currentVariantId];
            debugLog(`[VariantImageHover] Re-fetched hoverImageSrc: ${this.hoverImageSrc ? 'found' : 'not found'}`);
          }
          
          // If still no hover image, try to update variant
          if (!this.hoverImageSrc && this.currentVariantId) {
            debugWarn(`[VariantImageHover] No hover image for variant ${this.currentVariantId}, trying to update...`);
            this.updateCurrentVariant(this.currentVariantId);
            this.hoverImageSrc = this.variantImageMap[this.currentVariantId];
          }
          
          if (!this.hoverImageSrc) {
            debugWarn('[VariantImageHover] Cannot toggle: no hover image available');
            debugWarn('[VariantImageHover] Variant map:', Object.keys(this.variantImageMap));
            return;
          }

          // CRITICAL: Check if we have a stored sideways image for this variant first
          // This is the correct original/default view when featured image is Front
          const originalImageKey = `${this.currentVariantId}_original`;
          if (this.variantImageMap[originalImageKey]) {
            // Use stored sideways image as original
            this.originalImageSrc = this.variantImageMap[originalImageKey];
            debugLog(`[VariantImageHover] Using stored sideways image as original for toggle: ${this.originalImageSrc.split('/').pop()}`);
          } else {
            // No stored sideways image - featured image should be sideways (normal case)
            let featuredImageSrc = null;
            
            // Try to get from variant data
            if (this.currentVariantId && window.productVariants) {
              const productId = document.querySelector('[data-product-id]')?.dataset?.productId;
              if (productId && window.productVariants[productId]) {
                const variant = window.productVariants[productId].find(v => v.id.toString() === this.currentVariantId);
                if (variant && variant.featured_image) {
                  featuredImageSrc = variant.featured_image.src || variant.featured_image;
                  debugLog(`[VariantImageHover] Got featured image from variant data: ${featuredImageSrc.split('/').pop()}`);
                }
              }
            }
            
            // Fallback: Use current image only if it's NOT the hover image
            if (!featuredImageSrc) {
              const currentImage = this.mainImageElement.src || this.mainImageElement.currentSrc;
              const currentUrl = currentImage.split('?')[0];
              const hoverUrl = this.hoverImageSrc.split('?')[0];
              
              // If current image is NOT the hover image, use it as original (compare URLs, not filenames)
              if (currentUrl !== hoverUrl) {
                featuredImageSrc = currentImage;
                debugLog(`[VariantImageHover] Using current image as original (sideways view): ${currentUrl}`);
              }
            }
            
            // Store featured image as original
            if (featuredImageSrc) {
              this.originalImageSrc = featuredImageSrc.split('?')[0]; // Remove query params
              debugLog(`[VariantImageHover] Storing featured image as original: ${this.originalImageSrc.split('/').pop()}`);
            } else {
              // Last resort: use current image
              const currentImage = this.mainImageElement.src || this.mainImageElement.currentSrc;
              this.originalImageSrc = currentImage;
              debugWarn(`[VariantImageHover] Using current image as fallback: ${currentImage.split('/').pop()}`);
            }
          }
          
          debugLog(`[VariantImageHover] Toggling to front view (hover view) for variant ${this.currentVariantId}`);
          
          // Set flag before switching (switchToHoverImage will also set it, but set it here too for toggle)
          this._isChangingImage = true;
          
          this.isToggled = true;
          this.switchToHoverImage();
          
          if (this.viewToggleButton) {
            const textElement = this.viewToggleButton.querySelector('.variant-view-toggle__text');
            if (textElement) {
              textElement.textContent = 'View Sideways';
            }
            this.viewToggleButton.classList.add('is-active');
          }
        } else {
          // Switching BACK to sideways view (default view)
          // Note: Default = Sideways alt tag, Hover = Front alt tag
          // Set flag to prevent watchForImageChanges from interfering
          this._isChangingImage = true;
          
          if (this.mainImageElement && this.originalImageSrc) {
            this.mainImageElement.src = this.originalImageSrc;
            debugLog(`[VariantImageHover] Toggled back to sideways view: ${this.originalImageSrc.split('/').pop()}`);
          } else {
            // Fallback: get current variant's featured image
            // Re-find main image and use it as original
            debugWarn('[VariantImageHover] No originalImageSrc, re-finding main image...');
            this.findMainImage();
            if (this.mainImageElement) {
              const currentImage = this.mainImageElement.src || this.mainImageElement.currentSrc;
              this.mainImageElement.src = currentImage;
              this.originalImageSrc = currentImage;
              debugLog(`[VariantImageHover] Used fallback image: ${currentImage.split('/').pop()}`);
            } else {
              debugError('[VariantImageHover] Cannot restore: no main image element');
              // Try to re-setup handlers
              setTimeout(() => {
                this.setupHoverHandlers();
              }, 500);
              return;
            }
          }
          
          this.isToggled = false;
          
          if (this.viewToggleButton) {
            const textElement = this.viewToggleButton.querySelector('.variant-view-toggle__text');
            if (textElement) {
              textElement.textContent = 'View Front';
            }
            this.viewToggleButton.classList.remove('is-active');
          }
          
          // Clear flag after a short delay
          setTimeout(() => {
            this._isChangingImage = false;
          }, 100);
        }
      }

      ensureSidewaysView() {
        // Ensure the default displayed image is always sideways, not front
        // AGGRESSIVE: Always switch to sideways if we can find one, regardless of current image
        
        if (!this.mainImageElement) {
          debugLog('[VariantImageHover] ensureSidewaysView: No main image element, finding...');
          this.findMainImage();
          if (!this.mainImageElement) {
            debugWarn('[VariantImageHover] ensureSidewaysView: Still no main image element');
            return false;
          }
        }

        const currentSrc = this.mainImageElement.src || this.mainImageElement.currentSrc || '';
        if (!currentSrc) {
          debugWarn('[VariantImageHover] ensureSidewaysView: No current image source');
          return false;
        }

        const currentUrl = currentSrc.split('?')[0];
        const currentAlt = (this.mainImageElement.alt || '').toLowerCase();
        
        debugLog(`[VariantImageHover] ensureSidewaysView: Current URL: ${currentUrl}, Alt: "${currentAlt}"`);

        // Strategy 1: Use stored sideways image from variant map (MOST RELIABLE)
        // IMPORTANT: Don't run if we're hovering or toggled (user is intentionally viewing front)
        if (this.isHovering || this.isToggled) {
          debugLog(`[VariantImageHover] ensureSidewaysView: Skipping - user is hovering or toggled`);
          return false;
        }
        
        if (this.currentVariantId) {
          const originalImageKey = `${this.currentVariantId}_original`;
          if (this.variantImageMap[originalImageKey]) {
            const sidewaysUrl = this.variantImageMap[originalImageKey];
            
            // ALWAYS switch to sideways if we have it stored, unless we're already showing it
            // Compare URLs, not filenames
            if (currentUrl !== sidewaysUrl) {
              debugLog(`[VariantImageHover] ensureSidewaysView: ✅ Switching to stored sideways image`);
              debugLog(`  From: ${currentUrl}`);
              debugLog(`  To: ${sidewaysUrl}`);
              
              // Set flag to prevent watchForImageChanges from interfering
              this._isChangingImage = true;
              this.mainImageElement.src = sidewaysUrl;
              this.originalImageSrc = sidewaysUrl;
              
              setTimeout(() => {
                this._isChangingImage = false;
              }, 100);
              
              return true;
            } else {
              debugLog(`[VariantImageHover] ensureSidewaysView: ✅ Already showing sideways view: ${sidewaysUrl}`);
              this.originalImageSrc = sidewaysUrl;
              return true;
            }
          }
        }

        // Strategy 2: Find sideways image by alt tag (works even without variant map)
        debugLog(`[VariantImageHover] ensureSidewaysView: Searching for sideways image by alt tag...`);
        const allImages = document.querySelectorAll('.product__media img, .gallery-slider img, .swiper-slide img, .gallery img');
        
        // First, try to find sideways image matching current variant's color/handlebar
        let sidewaysImage = null;
        
        // Extract color and handlebar from current image alt or variant
        let targetColor = '';
        let targetHandlebar = '';
        
        if (currentAlt) {
          const colorMatch = currentAlt.match(/\b(black|white|green|blue|red|yellow|orange|purple|pink|gray|grey)\b/);
          const handlebarMatch = currentAlt.match(/\b(bullhorn|flat bar|flat)\b/);
          if (colorMatch) targetColor = colorMatch[1];
          if (handlebarMatch) targetHandlebar = handlebarMatch[1];
        }
        
        // If we have variant info, use it
        if (!targetColor && this.currentVariantId && window.productVariants) {
          const productId = document.querySelector('[data-product-id]')?.dataset?.productId;
          if (productId && window.productVariants[productId]) {
            const variant = window.productVariants[productId].find(v => v.id.toString() === this.currentVariantId);
            if (variant) {
              targetColor = (variant.option1 || '').toLowerCase();
              targetHandlebar = (variant.option3 || '').toLowerCase();
            }
          }
        }
        
        debugLog(`[VariantImageHover] ensureSidewaysView: Looking for sideways image with color="${targetColor}", handlebar="${targetHandlebar}"`);
        
        // Search all images for sideways view
        for (const img of allImages) {
          const imgAlt = (img.alt || '').toLowerCase();
          const imgSrc = img.src || img.currentSrc || '';
          const imgUrl = imgSrc.split('?')[0];
          
          // Check if this is a sideways image - USE ALT TAGS ONLY
          // Trust alt tags completely (if alt tags are wrong, fix them in Shopify)
          const isSideways = imgAlt.includes('sideways') && !imgAlt.includes('front');
          
          if (isSideways) {
            // If we have color/handlebar info, match it
            if (targetColor && targetHandlebar) {
              const matchesColor = imgAlt.includes(targetColor);
              const matchesHandlebar = imgAlt.includes(targetHandlebar) || 
                                     (targetHandlebar.includes('flat') && imgAlt.includes('flat')) ||
                                     (targetHandlebar.includes('bullhorn') && (imgAlt.includes('bullhorn') || imgAlt.includes('bull')));
              
              if (matchesColor && matchesHandlebar) {
                sidewaysImage = { url: imgUrl, alt: imgAlt };
                debugLog(`[VariantImageHover] ensureSidewaysView: ✅ Found matching sideways image: ${imgUrl}`);
                break;
              }
            } else {
              // No color/handlebar info, use first sideways image found
              if (!sidewaysImage) {
                sidewaysImage = { url: imgUrl, alt: imgAlt };
                debugLog(`[VariantImageHover] ensureSidewaysView: Found sideways image (no color match): ${imgUrl}`);
              }
            }
          }
        }
        
        // If we found a sideways image, switch to it
        if (sidewaysImage) {
          // Only switch if it's different from current (compare URLs, not filenames)
          if (currentUrl !== sidewaysImage.url) {
            debugLog(`[VariantImageHover] ensureSidewaysView: ✅ Switching to sideways image`);
            debugLog(`  From: ${currentUrl}`);
            debugLog(`  To: ${sidewaysImage.url}`);
            
            // Set flag to prevent watchForImageChanges from interfering
            this._isChangingImage = true;
            this.mainImageElement.src = sidewaysImage.url;
            this.originalImageSrc = sidewaysImage.url;
            
            // Store it in variant map if we have a variant ID
            if (this.currentVariantId) {
              this.variantImageMap[`${this.currentVariantId}_original`] = sidewaysImage.url;
              debugLog(`[VariantImageHover] ensureSidewaysView: Stored sideways image in variant map`);
            }
            
            setTimeout(() => {
              this._isChangingImage = false;
            }, 100);
            
            return true;
          } else {
            debugLog(`[VariantImageHover] ensureSidewaysView: ✅ Already showing sideways image`);
            this.originalImageSrc = sidewaysImage.url;
            return true;
          }
        }

        // Strategy 3: Check if current image is front (by alt tag only)
        // USE ALT TAGS ONLY - if alt tags are wrong, fix them in Shopify
        const isCurrentFront = currentAlt.includes('front') && !currentAlt.includes('sideways');
        
        if (isCurrentFront) {
          debugWarn(`[VariantImageHover] ensureSidewaysView: ⚠️ Current image appears to be front, but no sideways image found`);
          debugWarn(`[VariantImageHover]   Current URL: ${currentUrl}, Alt: "${currentAlt}"`);
          debugWarn(`[VariantImageHover]   Searched ${allImages.length} images`);
        } else {
          debugLog(`[VariantImageHover] ensureSidewaysView: Current image appears to be sideways or cannot determine`);
        }
        
        return false;
      }

      updateButtonVisibility() {
        if (!this.viewToggleButton) return;

        // Show button if we have hover image, or if we're still building the map
        // Don't hide it immediately - give it time to find the image
        if (this.hoverImageSrc) {
          this.viewToggleButton.style.display = 'flex';
          debugLog('[VariantImageHover] Button visible - hover image available');
        } else if (Object.keys(this.variantImageMap).length > 0) {
          // If we have variant mappings but no hover image for current variant, still show button
          // It might be that this variant doesn't have a front image (hover view), but others do
          this.viewToggleButton.style.display = 'flex';
          debugLog('[VariantImageHover] Button visible - variant mappings exist');
        } else {
          // Only hide if we truly have no mappings at all
          this.viewToggleButton.style.display = 'none';
          debugLog('[VariantImageHover] Button hidden - no variant mappings found');
        }
      }
    }
  );
}
